import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { 
  ArrowLeft, 
  Search, 
  Image as ImageIcon,
  Upload,
  Trash2,
  ChevronLeft,
  ChevronRight
} from "lucide-react";

const API = "/api";

export default function FotografEklePage() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [urunler, setUrunler] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUrun, setSelectedUrun] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  
  // Fotoğraf upload
  const [selectedRenk, setSelectedRenk] = useState(null);
  const [anaFoto, setAnaFoto] = useState(null);
  const [digerFotolar, setDigerFotolar] = useState([]);

  useEffect(() => {
    fetchUrunler();
  }, [token, page, searchTerm]);

  const fetchUrunler = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/nebim/products?token=${token}&page=${page}&limit=20&search=${searchTerm}`);
      if (response.data.success) {
        const products = response.data.products || [];
        // Ürünleri ProductCode bazında grupla
        const grouped = {};
        products.forEach(p => {
          if (!grouped[p.ProductCode]) {
            grouped[p.ProductCode] = {
              ...p,
              renkler: []
            };
          }
          if (p.ColorCode && !grouped[p.ProductCode].renkler.find(r => r.kod === p.ColorCode)) {
            grouped[p.ProductCode].renkler.push({
              kod: p.ColorCode,
              ad: p.ColorDescription || p.ColorCode
            });
          }
        });
        setUrunler(Object.values(grouped));
        setHasMore(products.length === 20);
      }
    } catch (error) {
      console.error("Ürünler alınamadı:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUrunSec = (urun) => {
    setSelectedUrun(urun);
    setSelectedRenk(urun.renkler[0] || null);
    setAnaFoto(null);
    setDigerFotolar([]);
  };

  const handleAnaFotoSec = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAnaFoto(file);
    }
  };

  const handleDigerFotoSec = (e) => {
    const files = Array.from(e.target.files);
    setDigerFotolar([...digerFotolar, ...files]);
  };

  const handleFotoSil = (index) => {
    setDigerFotolar(digerFotolar.filter((_, i) => i !== index));
  };

  const handleKaydet = async () => {
    if (!selectedUrun || !selectedRenk) {
      toast.error("Lütfen ürün ve renk seçin");
      return;
    }
    if (!anaFoto) {
      toast.error("Lütfen ana fotoğraf seçin");
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("urun_kodu", selectedUrun.ProductCode);
      formData.append("renk_kodu", selectedRenk.kod);
      formData.append("ana_foto", anaFoto);
      digerFotolar.forEach((foto, idx) => {
        formData.append(`diger_foto_${idx}`, foto);
      });

      const response = await axios.post(`${API}/nebim/fotograf-yukle?token=${token}`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      if (response.data.success) {
        toast.success("Fotoğraflar yüklendi");
        setAnaFoto(null);
        setDigerFotolar([]);
      } else {
        toast.error(response.data.error || "Yükleme başarısız");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Yükleme hatası");
    } finally {
      setLoading(false);
    }
  };

  // Detay view
  if (selectedUrun) {
    return (
      <div className="space-y-6 fade-in" data-testid="fotograf-ekle-detay">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => setSelectedUrun(null)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Fotoğraf Ekle</h1>
            <p className="text-slate-500 text-sm">{selectedUrun.ProductCode} - {selectedUrun.ProductName}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Renk Seçimi */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Renk Seçimi</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {selectedUrun.renkler.map((renk, idx) => (
                  <Button
                    key={idx}
                    variant={selectedRenk?.kod === renk.kod ? "default" : "outline"}
                    onClick={() => setSelectedRenk(renk)}
                    className={selectedRenk?.kod === renk.kod ? "bg-emerald-500" : ""}
                  >
                    {renk.ad}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Fotoğraf Yükleme */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Fotoğraflar</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Ana Fotoğraf */}
              <div className="space-y-2">
                <Label>Ana Fotoğraf (1 Adet)</Label>
                <div className="border-2 border-dashed rounded-lg p-6 text-center">
                  {anaFoto ? (
                    <div className="relative inline-block">
                      <img 
                        src={URL.createObjectURL(anaFoto)} 
                        alt="Ana foto" 
                        className="max-w-full h-32 object-contain rounded"
                      />
                      <Button 
                        size="icon" 
                        variant="destructive" 
                        className="absolute -top-2 -right-2 h-6 w-6"
                        onClick={() => setAnaFoto(null)}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  ) : (
                    <label className="cursor-pointer">
                      <Upload className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                      <p className="text-sm text-slate-500">Tıklayın veya sürükleyin</p>
                      <p className="text-xs text-slate-400">JPG formatına çevrilecek</p>
                      <input 
                        type="file" 
                        accept="image/*" 
                        className="hidden" 
                        onChange={handleAnaFotoSec}
                      />
                    </label>
                  )}
                </div>
              </div>

              {/* Diğer Fotoğraflar */}
              <div className="space-y-2">
                <Label>Diğer Fotoğraflar (Çoklu)</Label>
                <div className="border-2 border-dashed rounded-lg p-4">
                  <div className="flex flex-wrap gap-2 mb-4">
                    {digerFotolar.map((foto, idx) => (
                      <div key={idx} className="relative">
                        <img 
                          src={URL.createObjectURL(foto)} 
                          alt={`Foto ${idx + 1}`} 
                          className="w-20 h-20 object-cover rounded"
                        />
                        <Button 
                          size="icon" 
                          variant="destructive" 
                          className="absolute -top-2 -right-2 h-5 w-5"
                          onClick={() => handleFotoSil(idx)}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                    <label className="w-20 h-20 border-2 border-dashed rounded flex items-center justify-center cursor-pointer hover:bg-slate-50">
                      <Upload className="w-6 h-6 text-slate-400" />
                      <input 
                        type="file" 
                        accept="image/*" 
                        multiple
                        className="hidden" 
                        onChange={handleDigerFotoSec}
                      />
                    </label>
                  </div>
                </div>
              </div>

              <Button 
                className="w-full bg-emerald-600 hover:bg-emerald-700"
                disabled={loading || !anaFoto}
                onClick={handleKaydet}
              >
                {loading ? "Yükleniyor..." : "Kaydet"}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Liste view
  return (
    <div className="space-y-6 fade-in" data-testid="fotograf-ekle-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Fotoğraf Ekle</h1>
          <p className="text-slate-500 text-sm">Ürünlere fotoğraf ekleyin</p>
        </div>
      </div>

      {/* Arama */}
      <div className="relative">
        <Search className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
        <Input
          value={searchTerm}
          onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
          placeholder="Ürün ara..."
          className="pl-10"
          data-testid="search-input"
        />
      </div>

      {/* Ürün Listesi */}
      <Card>
        <CardContent className="p-0">
          <div className="divide-y">
            {urunler.map((urun, idx) => (
              <div key={idx} className="flex items-center justify-between p-4 hover:bg-slate-50">
                <div className="flex-1">
                  <p className="font-medium">{urun.ProductCode}</p>
                  <p className="text-sm text-slate-500">{urun.ProductName}</p>
                  <div className="flex gap-1 mt-1">
                    {urun.renkler.slice(0, 5).map((renk, i) => (
                      <Badge key={i} variant="outline" className="text-xs">
                        {renk.ad}
                      </Badge>
                    ))}
                    {urun.renkler.length > 5 && (
                      <Badge variant="outline" className="text-xs">
                        +{urun.renkler.length - 5}
                      </Badge>
                    )}
                  </div>
                </div>
                <Button 
                  onClick={() => handleUrunSec(urun)}
                  className="bg-orange-500 hover:bg-orange-600"
                >
                  <ImageIcon className="w-4 h-4 mr-2" />
                  Fotoğraf Ekle
                </Button>
              </div>
            ))}
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between p-4 border-t">
            <Button 
              variant="outline" 
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Önceki
            </Button>
            <span className="text-sm text-slate-500">Sayfa {page}</span>
            <Button 
              variant="outline" 
              onClick={() => setPage(p => p + 1)}
              disabled={!hasMore}
            >
              Sonraki
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
